import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PhotoRecordDto } from '@forest/contracts';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useUiStore } from '@/stores/uiStore';
import { useNearbyParcels, useAssignParcel } from './usePhotos';

interface AssignParcelDialogProps {
  photo: PhotoRecordDto | null;
  onClose: () => void;
}

export function AssignParcelDialog({ photo, onClose }: AssignParcelDialogProps) {
  const { t } = useTranslation(['photos']);
  const pushToast = useUiStore((s) => s.pushToast);
  // Two-input debounce: `search` is what the user is typing right now,
  // `debouncedSearch` is what the server sees. Keeps the API quiet
  // while the user is still typing, and the list smooth.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(handle);
  }, [search]);
  // Reset the search when the dialog re-opens on a different photo.
  useEffect(() => {
    if (photo === null) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [photo]);
  const nearbyQuery = useNearbyParcels(photo?.id ?? null, debouncedSearch);
  const assignMut = useAssignParcel();

  const handleAssign = async (parcelId: string) => {
    if (!photo) return;
    try {
      await assignMut.mutateAsync({ photoId: photo.id, parcelId });
      pushToast({ variant: 'success', message: t('photos:assign.success') });
      onClose();
    } catch (e) {
      pushToast({
        variant: 'error',
        message: (e as Error).message,
      });
    }
  };

  const items = nearbyQuery.data?.items ?? [];

  return (
    <Dialog
      open={photo !== null}
      onClose={onClose}
      title={t('photos:assign.title')}
      size="lg"
    >
      {photo ? (
        <div className="space-y-3">
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="font-semibold text-slate-900">{photo.fileName}</div>
            <div className="text-xs text-slate-500">
              {photo.latitude.toFixed(6)}, {photo.longitude.toFixed(6)}
            </div>
          </div>

          <p className="text-sm text-slate-600">{t('photos:assign.subtitle')}</p>

          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('photos:assign.searchPlaceholder', {
              defaultValue: 'Caută după raion / primărie / UCF / cod proiect...',
            })}
            autoFocus
          />

          {nearbyQuery.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title={t('photos:assign.noResults')}
              description=""
            />
          ) : (
            <div className="max-h-80 overflow-auto rounded border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">Raion</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Primarie</th>
                    <th className="px-2 py-1.5 text-left font-semibold">UCF</th>
                    <th className="px-2 py-1.5 text-left font-semibold">
                      {t('photos:assign.codProiect', { defaultValue: 'Cod proiect' })}
                    </th>
                    <th className="px-2 py-1.5 text-right font-semibold">ha</th>
                    <th className="px-2 py-1.5 text-right font-semibold">An</th>
                    <th className="px-2 py-1.5 text-right font-semibold">
                      {t('photos:assign.distance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => void handleAssign(p.id)}
                      className={
                        p.contains
                          ? 'cursor-pointer border-t border-slate-100 bg-emerald-50/60 hover:bg-emerald-100 transition-colors'
                          : 'cursor-pointer border-t border-slate-100 hover:bg-amber-50 transition-colors'
                      }
                    >
                      <td className="px-2 py-1.5">
                        {p.contains ? (
                          <span className="mr-1 inline-flex items-center rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                            {t('photos:assign.contains', { defaultValue: 'Înăuntru' })}
                          </span>
                        ) : null}
                        {p.raion}
                      </td>
                      <td className="px-2 py-1.5">{p.primarie}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{p.ucf}</td>
                      <td className="px-2 py-1.5 font-mono text-xs text-slate-500">
                        {p.codProiect ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {p.suprafata}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.anul}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {p.contains ? '—' : `${p.distanceKm.toFixed(1)} ${t('photos:assign.km')}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {assignMut.isPending ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Spinner />
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
